import React, { useState, useEffect, useCallback, useRef } from "react";
import { Truck, User, CircleDashed, ClipboardList, Download, Upload, Plus, X, FileText, AlertTriangle, History, Search } from "lucide-react";
import API from "../services/api";
import EditableCell from "./runsheets/EditableCell";
import RunDetail from "./runsheets/RunDetail";
import PoolRow from "./runsheets/PoolRow";
import { Highlight, matchesAll } from "./ProductFinder";
import { useCanEdit, ReadOnlyBanner } from "../services/permissions";
import { downloadWithAuth, errMsg, nzToday, addDay, deliveryOf, prettyDate } from "./runsheets/util";

export default function RunSheets({ onBack }) {
  const canEdit = useCanEdit();
  const [activeTab, setActiveTab] = useState("standard"); // 'standard' | 'harvey' | 'manual'
  const [runs, setRuns] = useState([]);
  const [harveyRuns, setHarveyRuns] = useState([]);
  const [showMerged, setShowMerged] = useState(false);
  const [mergedPlan, setMergedPlan] = useState([]);
  const [mergedDateFilter, setMergedDateFilter] = useState("");   // "" = all delivery dates
  const [mergedNextDay, setMergedNextDay] = useState("");         // NZ tomorrow, from API
  const [hnFilter, setHnFilter] = useState("");                   // client-side: product / customer / address / store
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [extractionStatus, setExtractionStatus] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [manualJobs, setManualJobs] = useState([]);       // orders created by uploads this session
  const [uploading, setUploading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newRunName, setNewRunName] = useState("");
  const [newRunDate, setNewRunDate] = useState(() => nzToday());
  const [creating, setCreating] = useState(false);
  const [runDateFilter, setRunDateFilter] = useState("");   // "" = all loaded dates
  const [showOlder, setShowOlder] = useState(false);        // days=0: the whole history
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);               // { message, runId }

  // Every loader reports here instead of swallowing into console.error, so
  // an empty panel always comes with the server's reason. Stable identities:
  // RunDetail keys its fetch effects off these.
  const reportError = useCallback((err) => setError(errMsg(err)), []);
  const notify = useCallback((message, runId) => setNotice({ message, runId: runId || null }), []);
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  // The list is a working view (last 7 days + anything upcoming). "Show
  // older" asks for everything; a delivery-date filter that points before
  // the loaded window fetches just that window, so the operator is never
  // told "no runs" for a day that does exist.
  const fetchRuns = useCallback(async () => {
    let url = "/runs";
    const oldest = addDay(nzToday(), -6);   // earliest delivery day the default window covers
    if (showOlder) url += "?days=0";
    else if (runDateFilter && runDateFilter < oldest) {
      url += `?date_from=${addDay(runDateFilter, -3)}&date_to=${addDay(runDateFilter, 1)}`;
    }
    try {
      // no trailing slash: FastAPI 307-redirects "/runs/" to an absolute
      // localhost URL, which the browser cannot follow through nginx - this
      // was why the runs list (and Create Run) appeared dead in production
      const res = await API.get(url);
      setRuns(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      reportError(err);
    } finally {
      setLoading(false);
    }
  }, [showOlder, runDateFilter, reportError]);
  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const handleCreateRun = async () => {
    setCreating(true);
    try {
      const res = await API.post("/runs", { name: newRunName.trim() || null, date: newRunDate, status: "draft" });
      setShowCreate(false);
      setNewRunName("");
      await fetchRuns();
      // land straight in the new run so stops can be assigned immediately
      if (res.data?.id) setSelectedRunId(res.data.id);
    } catch (err) {
      reportError(err);
    } finally {
      setCreating(false);
    }
  };

  const fetchLatestRuns = useCallback(async () => {
    try {
      const res = await API.get("/scraper/latest-runs");
      if (res.data && Array.isArray(res.data.data)) setHarveyRuns(res.data.data);
      const statusRes = await API.get("/scraper/status");
      if (statusRes.data) {
        setExtractionStatus(statusRes.data);
        setExtracting(statusRes.data.status === "extracting");
      }
    } catch (err) {
      reportError(err);
    }
  }, [reportError]);

  // Read once on load and whenever the operator lands on the tab...
  const onHarvey = activeTab === "harvey";
  useEffect(() => { fetchLatestRuns(); }, [onHarvey, fetchLatestRuns]);

  // ...and poll ONLY while the portal extraction is running (on any tab, so
  // the "Extracting..." button clears wherever it was pressed). Polling the
  // whole time re-read the persisted rows every 5s and threw away whatever
  // correction the operator had just typed.
  useEffect(() => {
    if (!extracting) return undefined;
    const id = setInterval(fetchLatestRuns, 5000);
    return () => clearInterval(id);
  }, [extracting, fetchLatestRuns]);

  // One last read after the extraction finishes: the poll that noticed the
  // status flip fetched the rows before the status, so it can be a cycle old.
  const wasExtracting = useRef(false);
  useEffect(() => {
    if (wasExtracting.current && !extracting) fetchLatestRuns();
    wasExtracting.current = extracting;
  }, [extracting, fetchLatestRuns]);

  const loadMergedPlan = async (dateFilter) => {
    try {
      const q = dateFilter ? `?date=${encodeURIComponent(dateFilter)}` : "";
      const res = await API.get(`/scraper/merged-plan${q}`);
      setMergedPlan(res.data?.data || []);
      if (res.data?.next_day) setMergedNextDay(res.data.next_day);
    } catch (err) {
      reportError(err);
    }
  };
  const toggleMerged = async () => {
    if (!showMerged) await loadMergedPlan(mergedDateFilter);
    setShowMerged(v => !v);
  };
  const applyMergedDate = async (d) => {
    setMergedDateFilter(d);
    await loadMergedPlan(d);
  };

  // Persist a corrected Harvey Norman cell on the server row (that list is
  // what the Excel/PDF exports and the merged plan read), then log it as a
  // training correction. Before this the edit only lived in React state and
  // the next poll wiped it.
  // jobNo travels with the edit so a screen loaded before a re-extraction can
  // never correct whatever job now sits at the same index (the server answers
  // 409 and the list is refetched).
  const handleCorrection = async (index, field, originalValue, correctedValue, jobNo) => {
    try {
      const res = await API.patch(`/scraper/latest-runs/${index}`, { field, value: correctedValue, job_no: jobNo || "" });
      const body = res.data || {};
      const row = body[field] !== undefined ? body
        : (body.data && body.data[field] !== undefined ? body.data : { [field]: correctedValue });
      setHarveyRuns(prev => prev.map((hr, i) => (i === index ? { ...hr, ...row } : hr)));
    } catch (err) {
      reportError(err);
      if (err?.response?.status === 409) fetchLatestRuns();
      return;
    }
    try {
      await API.post("/ai/corrections", {
        original_value: originalValue,
        corrected_value: correctedValue,
        column_name: field
      });
    } catch { /* training is best-effort; the sheet itself is already right */ }
  };

  const triggerExtraction = async () => {
    setExtracting(true);
    setExtractionStatus({ status: "extracting", message: "Starting extraction in background..." });
    try {
      await API.post("/scraper/extract-runs");
    } catch (err) {
      reportError(err);
      setExtractionStatus({ status: "error", message: "Failed to communicate with extraction server." });
      setExtracting(false);
    }
  };

  const download = async (path, filename) => {
    try { await downloadWithAuth(path, filename); }
    catch (err) { reportError(err); }
  };

  // Customer deliveries live on this tab (not the Orders dashboard) until an
  // operator assigns them to a daily run
  const [custDeliveries, setCustDeliveries] = useState([]);
  const [cdDate, setCdDate] = useState("");
  const loadCustDeliveries = useCallback(async () => {
    try {
      let url = "/jobs?otype=customer_delivery&unassigned=1&limit=200&sort=email_date_iso&order=desc";
      if (cdDate) url += `&date_from=${cdDate}&date_to=${cdDate}`;
      const res = await API.get(url);
      setCustDeliveries(res.data?.orders || []);
    } catch (err) {
      reportError(err);
    }
  }, [cdDate, reportError]);
  useEffect(() => { loadCustDeliveries(); }, [loadCustDeliveries]);

  // A stop assigned from the pool leaves it and bumps a run's count
  const onPoolChanged = () => { loadCustDeliveries(); fetchRuns(); };

  // Manual imports: after an edit re-read the order so the row shows the
  // corrected fields; after an assign it belongs to a run and leaves the list.
  const onManualChanged = async (action, jobId) => {
    if (action === "assigned") {
      setManualJobs(prev => prev.filter(j => j.id !== jobId));
      fetchRuns();
      loadCustDeliveries();
      return;
    }
    try {
      const res = await API.get(`/jobs/${encodeURIComponent(jobId)}`);
      setManualJobs(prev => prev.map(j => (j.id === jobId ? res.data : j)));
    } catch (err) {
      reportError(err);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await processFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;
    await processFiles(files);
    e.target.value = null;
  };

  const processFiles = async (files) => {
    setUploading(true);
    let created = 0;
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await API.post("/parser/llm-upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        // the server answers with the saved orders (ids included), so they
        // get the same Edit / Assign controls as the unassigned pool rather
        // than a local-only table that forgot them on the next reload
        const orders = [].concat(res.data?.data || res.data?.order || []).filter(o => o && o.id);
        created += orders.length;
        setManualJobs(prev => [...orders, ...prev.filter(p => !orders.some(o => o.id === p.id))]);
      }
      notify(`Parsed ${files.length} document${files.length === 1 ? "" : "s"} — ${created} order${created === 1 ? "" : "s"} created`);
      loadCustDeliveries();
    } catch (err) {
      reportError(err);
    } finally {
      setUploading(false);
    }
  };

  // Shown above whichever view is open, so a failed save in the run detail
  // is as visible as one in the list.
  const banners = (error || notice) ? (
    <div className="px-6 pt-4 max-w-[1700px] mx-auto space-y-2">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1 break-words">{error}</span>
          <button onClick={() => setError(null)} className="text-red-300/70 hover:text-white"><X size={14} /></button>
        </div>
      )}
      {notice && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm text-emerald-300 flex items-center gap-2">
          <span className="flex-1">{notice.message}</span>
          {notice.runId && notice.runId !== selectedRunId && (
            <button onClick={() => { setSelectedRunId(notice.runId); setNotice(null); }}
              className="text-emerald-200 hover:text-white underline whitespace-nowrap">Open run →</button>
          )}
          <button onClick={() => setNotice(null)} className="text-emerald-300/70 hover:text-white"><X size={14} /></button>
        </div>
      )}
    </div>
  ) : null;

  if (loading) return <div className="p-8 text-center text-slate-400">Loading Runs...</div>;

  if (selectedRunId) {
    return (
      <>
        {banners}
        <RunDetail
          runId={selectedRunId}
          runs={runs}
          onBack={() => setSelectedRunId(null)}
          onChanged={onPoolChanged}
          onOpenRun={(id) => setSelectedRunId(id)}
          reportError={reportError}
          notify={notify}
        />
      </>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {banners}
      {!canEdit && <ReadOnlyBanner />}
      {canEdit && showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Create New Run</h3>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            <label className="block text-xs text-slate-400 mb-1">Run name (optional)</label>
            <input autoFocus value={newRunName} onChange={(e) => setNewRunName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateRun()}
              placeholder="e.g. Monday South Run"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm mb-4 outline-none focus:border-blue-500" />
            <label className="block text-xs text-slate-400 mb-1">Run date</label>
            <input type="date" value={newRunDate} onChange={(e) => setNewRunDate(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm mb-5 outline-none focus:border-blue-500 [&::-webkit-calendar-picker-indicator]:invert" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800">Cancel</button>
              <button onClick={handleCreateRun} disabled={creating || !newRunDate}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50">
                {creating ? "Creating…" : "Create Run"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardList className="text-blue-500" />
            Dispatch Run Sheets
          </h2>
          <div className="flex bg-slate-800 rounded-lg p-1">
            <button 
              onClick={() => setActiveTab("standard")}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeTab === "standard" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              Standard Runs
            </button>
            <button 
              onClick={() => setActiveTab("harvey")}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeTab === "harvey" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              Harvey Norman Imports
            </button>
            <button 
              onClick={() => setActiveTab("manual")}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeTab === "manual" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              Email / Manual Imports
            </button>
          </div>
        </div>
        {activeTab === "standard" ? (
          canEdit && (
            <button onClick={() => setShowCreate(true)}
              className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5">
              <Plus size={16} /> Create New Run
            </button>
          )
        ) : (
          <div className="flex items-center gap-4">
            {extractionStatus && extractionStatus.status === "error" && (
              <span className="text-red-500 font-medium text-sm max-w-md">
                Error: {extractionStatus.message}
              </span>
            )}
            {extractionStatus && extractionStatus.status === "extracting" && (
              <span className="text-blue-500 font-medium text-sm flex items-center gap-2">
                <CircleDashed className="animate-spin" size={16} />
                Extracting... this may take a minute.
              </span>
            )}
            {/* Pulling the portal rewrites the stored HN rows that the exports
                and the merged plan are built from - a write, not a refresh. */}
            {canEdit && (
              <button
                onClick={triggerExtraction}
                disabled={extracting}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                {extracting ? <CircleDashed className="animate-spin" size={18} /> : <Download size={18} />}
                {extracting ? "Extracting..." : "Fetch Latest Reports from Portal"}
              </button>
            )}
          </div>
        )}
      </div>

      {activeTab === "standard" && (
        <div className="bg-slate-800/50 p-4 rounded-xl border border-amber-500/30 mb-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider">
              Unassigned deliveries & stops — assign to a run ({custDeliveries.length})
            </h3>
            <div className="flex items-center gap-2 ml-auto text-xs">
              <span className="text-slate-500">Delivery date:</span>
              <input
                type="date"
                className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-slate-300 text-xs [&::-webkit-calendar-picker-indicator]:invert"
                value={cdDate}
                onChange={(e) => setCdDate(e.target.value)}
              />
              {cdDate && (
                <button onClick={() => setCdDate("")}
                  className="text-slate-400 hover:text-white border border-slate-700 rounded-lg px-2 py-1">
                  All dates
                </button>
              )}
            </div>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {custDeliveries.length === 0 && (
              <p className="text-slate-500 text-xs py-3 text-center">
                No unassigned customer deliveries{cdDate ? ` for ${cdDate}` : ""}.
              </p>
            )}
            {custDeliveries.map(cd => (
              <PoolRow key={cd.id} job={cd} runs={runs} hintDate={cdDate || null}
                onChanged={onPoolChanged} onError={reportError} onNotice={notify} />
            ))}
          </div>
        </div>
      )}

      {activeTab === "standard" && (() => {
        // Group/filter by the DELIVERY day. The backend computes it once
        // (runs.delivery_date): a sheet whose title carries an explicit date
        // ("Run Sheet 25/07/2026", "DELIVERY REPORT for 25/07/26") delivers on
        // that day; a dateless sheet ("Run") is received the day before, so it
        // delivers on run.date + 1. Falling back to the old +1 (deliveryOf)
        // keeps older clients working if the field is missing.
        const filtered = runDateFilter ? runs.filter(r => deliveryOf(r) === runDateFilter) : runs;
        const byDate = {};
        filtered.forEach(r => { const k = deliveryOf(r); (byDate[k] = byDate[k] || []).push(r); });
        const dates = Object.keys(byDate).sort((a, b) => String(b).localeCompare(String(a)));
        return (
          <div className="space-y-6">
            {/* Date filter — by delivery date (day the run is driven) */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold">Runs by delivery date</span>
              <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs">
                <Truck size={13} className="text-slate-500" />
                <input type="date" value={runDateFilter} onChange={e => setRunDateFilter(e.target.value)}
                  className="bg-transparent text-slate-300 outline-none [&::-webkit-calendar-picker-indicator]:invert" />
              </div>
              {runDateFilter && (
                <button onClick={() => setRunDateFilter("")}
                  className="text-slate-400 hover:text-white border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs transition-colors">
                  All dates
                </button>
              )}
              <button onClick={() => setShowOlder(v => !v)}
                title={showOlder ? "Back to the last 7 days" : "Load every run, not just the last 7 days"}
                className={`flex items-center gap-1.5 border rounded-lg px-2.5 py-1.5 text-xs transition-colors ${
                  showOlder ? "bg-slate-700 text-white border-slate-600" : "text-slate-400 hover:text-white border-slate-700"}`}>
                <History size={13} /> {showOlder ? "Showing all runs" : "Show older"}
              </button>
              <span className="text-xs text-slate-500 ml-auto">
                {filtered.length} run{filtered.length === 1 ? "" : "s"}{runDateFilter ? ` delivering ${runDateFilter}` : (showOlder ? "" : " · last 7 days")}
              </span>
            </div>

            {dates.length === 0 && (
              <div className="text-center p-12 text-slate-500 bg-slate-800/20 rounded-xl border border-slate-700 border-dashed">
                <ClipboardList size={48} className="mx-auto mb-4 opacity-20" />
                <p>{runDateFilter ? `No runs delivering ${prettyDate(runDateFilter)}.`
                  : showOlder ? "No runs created yet." : "No runs in the last 7 days — use \"Show older\" for the history."}</p>
              </div>
            )}

            {dates.map(d => {
              const dayRuns = byDate[d];
              const stops = dayRuns.reduce((s, r) => s + (r.job_count || 0), 0);
              return (
                <div key={d}>
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-sm font-bold text-white whitespace-nowrap">{prettyDate(d)}</h3>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 whitespace-nowrap">
                      {dayRuns.length} run{dayRuns.length === 1 ? "" : "s"} · {stops} stop{stops === 1 ? "" : "s"}
                    </span>
                    <div className="flex-1 h-px bg-slate-800"></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dayRuns.map(run => (
                      <div
                        key={run.id}
                        onClick={() => setSelectedRunId(run.id)}
                        className="bg-slate-800/50 p-5 rounded-xl border border-slate-700 cursor-pointer hover:bg-slate-800 hover:border-slate-600 hover:-translate-y-0.5 transition-all group"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
                            {run.name || `Run #${run.id}`}
                          </h3>
                          <span className="text-xs px-2 py-1 bg-slate-900 rounded text-slate-400 border border-slate-700 capitalize">
                            {run.status}
                          </span>
                        </div>
                        <div className="space-y-2 text-sm text-slate-400">
                          <div className="flex items-center gap-2">
                            <Truck size={14} className="text-emerald-500/70" />
                            <span>Deliver <span className="text-slate-200 font-medium">{deliveryOf(run)}</span></span>
                            {run.created_at && (
                              <span className="text-[10px] text-slate-600">· added {String(run.created_at).slice(0, 10)}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <User size={14} className="text-slate-500" />
                            {run.driver_name || (run.driver_id ? `Driver ${run.driver_id}` : "Driver TBD")}
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <ClipboardList size={14} className="text-blue-500/70" />
                            <span className="text-slate-300 font-semibold">{run.job_count || 0}</span> stop{run.job_count === 1 ? "" : "s"}
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-700/50 text-xs text-blue-400/80 group-hover:text-blue-400">
                          View details →
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {activeTab === "harvey" && (() => {
        // group current view by pickup store; _idx keeps each row's position
        // in the server list, which is what PATCH /scraper/latest-runs/{index}
        // edits - so it is stamped BEFORE the filter drops any rows
        const all = showMerged ? mergedPlan : harveyRuns;
        const pickupKey = showMerged ? "Pickup From" : "Store";
        // "Which van has the PROVINCE buffet?" - every word typed must appear
        // somewhere across the row's product, customer, address and store
        // columns (same rule as the product search on the Orders board).
        const filterFields = showMerged
          ? ["Products", "Customer", "Address Line", "Pickup From", "Store", "Invoice/PO"]
          : ["Products", "Customer", "Address Line", "Store", "Invoice/PO", "Job No"];
        const rowText = (r) => filterFields.map((f) => (r[f] == null ? "" : String(r[f]))).join(" | ");
        const filtering = !!hnFilter.trim();
        const source = all.map((r, i) => ({ ...r, _idx: i })).filter((r) => !filtering || matchesAll(rowText(r), hnFilter));
        const groups = {};
        source.forEach((r) => { const k = r[pickupKey] || "(unknown)"; (groups[k] = groups[k] || []).push(r); });
        const pickups = Object.keys(groups).sort((a, b) => a.localeCompare(b));
        const exportQuery = `?merged=${showMerged}${showMerged && mergedDateFilter ? `&date=${mergedDateFilter}` : ""}`;
        const exportStem = showMerged ? `final-plan-${mergedDateFilter || "next-day"}` : "harvey-norman-runs";
        const productLines = (v) => (v ? String(v).split("; ") : []);
        return (
        <div className="space-y-4">
          {/* Toolbar: fetch date, product filter, merge toggle, exports */}
          <div className="flex flex-wrap items-center gap-3 bg-slate-950/40 px-4 py-3 rounded-xl border border-slate-800">
            <div className="text-sm">
              <span className="text-slate-300 font-semibold">{showMerged ? "Final Plan (HN + Standard Runs)" : "Harvey Norman — Tomorrow's Jobs"}</span>
              <span className="text-slate-500 text-xs ml-2">
                {filtering
                  ? `${source.length} of ${all.length} job${all.length === 1 ? "" : "s"}`
                  : `${all.length} job${all.length === 1 ? "" : "s"}`}
                {" · "}{pickups.length} pickup point{pickups.length === 1 ? "" : "s"}
                {extractionStatus?.fetched_at && !showMerged && ` · fetched ${extractionStatus.fetched_at}`}
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs min-w-[280px] focus-within:border-blue-500 transition-colors">
              <Search size={13} className="text-slate-500 shrink-0" />
              <input
                type="text"
                value={hnFilter}
                onChange={(e) => setHnFilter(e.target.value)}
                placeholder="Filter by product, customer, address or store"
                className="bg-transparent text-slate-200 placeholder:text-slate-500 outline-none w-full"
              />
              {hnFilter && (
                <button type="button" onClick={() => setHnFilter("")} className="text-slate-500 hover:text-white shrink-0" title="Clear filter">
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {showMerged && (
                <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-xs">
                  <button onClick={() => applyMergedDate("")}
                    title="Only deliveries for the next-day run"
                    className={`px-2 py-1 rounded font-semibold transition-colors ${
                      (mergedDateFilter === "" || mergedDateFilter === mergedNextDay)
                        ? "bg-indigo-600 text-white" : "text-slate-300 hover:text-white"}`}>
                    Next day{mergedNextDay ? ` (${mergedNextDay.slice(5)})` : ""}
                  </button>
                  <input type="date" value={/^\d{4}-\d{2}-\d{2}$/.test(mergedDateFilter) ? mergedDateFilter : ""}
                    onChange={e => applyMergedDate(e.target.value)}
                    className="bg-transparent text-slate-300 outline-none [&::-webkit-calendar-picker-indicator]:invert" />
                  <button onClick={() => applyMergedDate("all")}
                    title="Every outstanding delivery, all dates"
                    className={`px-2 py-1 rounded font-semibold transition-colors ${
                      mergedDateFilter === "all" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}>
                    All dates
                  </button>
                </div>
              )}
              <button onClick={toggleMerged}
                className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-colors ${
                  showMerged ? "bg-indigo-600 text-white border-indigo-500" : "bg-slate-900 text-slate-300 border-slate-700 hover:border-indigo-500/40"}`}>
                <ClipboardList size={14} /> {showMerged ? "Showing Merged Plan" : "Merge with Standard Runs"}
              </button>
              <button onClick={() => download(`/scraper/export.xlsx${exportQuery}`, `${exportStem}.xlsx`)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                <Download size={14} /> Excel
              </button>
              <button onClick={() => download(`/scraper/export.pdf${exportQuery}`, `${exportStem}.pdf`)}
                className="bg-red-600/90 hover:bg-red-500 text-white px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                <FileText size={14} /> PDF
              </button>
            </div>
          </div>

          {/* Pickup-grouped table */}
          {all.length === 0 ? (
            <div className="bg-slate-800/20 rounded-xl border border-slate-700 border-dashed px-4 py-12 text-center text-slate-500">
              {showMerged ? "No jobs to merge yet." : 'No Harvey Norman jobs. Click "Fetch Latest Reports from Portal" to extract tomorrow\'s runs.'}
            </div>
          ) : source.length === 0 ? (
            <div className="bg-slate-800/20 rounded-xl border border-slate-700 border-dashed px-4 py-12 text-center text-slate-500">
              No jobs match “{hnFilter.trim()}”.{" "}
              <button type="button" onClick={() => setHnFilter("")} className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
                Clear the filter
              </button>
            </div>
          ) : pickups.map(pickup => (
            <div key={pickup} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="flex items-center gap-3 bg-slate-900/70 px-4 py-2 border-b border-slate-700">
                <Truck size={15} className="text-emerald-400/80" />
                <span className="font-bold text-white text-sm">{pickup}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{groups[pickup].length} stop{groups[pickup].length === 1 ? "" : "s"}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-300">
                  <thead className="bg-slate-900/40 text-slate-500 uppercase font-semibold text-[10px] border-b border-slate-700/60">
                    {showMerged ? (
                      <tr className="whitespace-nowrap">
                        <th className="px-3 py-2">#</th><th className="px-3 py-2">Deliver</th>
                        <th className="px-3 py-2">Source</th><th className="px-3 py-2">Invoice/PO</th>
                        <th className="px-3 py-2">S#</th><th className="px-3 py-2">Customer</th>
                        <th className="px-3 py-2">Address Line</th><th className="px-3 py-2">Warehouse</th>
                        <th className="px-3 py-2">Phone</th><th className="px-3 py-2">Pickup</th><th className="px-3 py-2">Store</th>
                        <th className="px-3 py-2">Products</th><th className="px-3 py-2">Instructions</th>
                      </tr>
                    ) : (
                      <tr className="whitespace-nowrap">
                        <th className="px-3 py-2">#</th><th className="px-3 py-2">Invoice/PO</th>
                        <th className="px-3 py-2">Customer</th><th className="px-3 py-2">Address Line</th>
                        <th className="px-3 py-2">Pickup store</th><th className="px-3 py-2">Phone</th>
                        <th className="px-3 py-2">Items</th><th className="px-3 py-2">Products</th>
                        <th className="px-3 py-2">Instructions</th><th className="px-3 py-2">Portal notes</th>
                      </tr>
                    )}
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {groups[pickup].map((hr, idx) => showMerged ? (
                      <tr key={idx} className="hover:bg-slate-800/80 align-top">
                        <td className="px-3 py-2.5 tnum text-slate-500">{hr["#"]}</td>
                        <td className="px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">{hr["Deliver Date"] || "—"}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${hr.Source === "HN Import" ? "bg-blue-500/15 text-blue-300" : "bg-violet-500/15 text-violet-300"}`}>{hr.Source}</span>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs text-indigo-400 whitespace-nowrap">{hr["Invoice/PO"]}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-slate-400 whitespace-nowrap">{hr["S#"]}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap"><Highlight text={hr.Customer} query={hnFilter} /></td>
                        <td className="px-3 py-2.5 text-white font-medium max-w-[220px] truncate" title={hr["Address Line"]}><Highlight text={hr["Address Line"]} query={hnFilter} /></td>
                        <td className="px-3 py-2.5 text-xs whitespace-nowrap">{hr.Warehouse}</td>
                        <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{hr.Phone}</td>
                        <td className="px-3 py-2.5 text-xs whitespace-nowrap font-semibold text-emerald-300">{hr["Pickup From"] || "—"}</td>
                        <td className="px-3 py-2.5 text-xs whitespace-nowrap">{hr.Store}</td>
                        <td className="px-3 py-2.5 text-xs min-w-[200px] max-w-[320px]" title={hr.Products}>
                          {productLines(hr.Products).map((line, i) => <div key={i}><Highlight text={line} query={hnFilter} /></div>)}
                        </td>
                        <td className="px-3 py-2.5 text-slate-400 text-xs min-w-[180px] max-w-[300px] whitespace-pre-wrap" title={hr.Instructions}>{hr.Instructions}</td>
                      </tr>
                    ) : (
                      <tr key={idx} className="hover:bg-slate-800/80 align-top">
                        <td className="px-3 py-2.5 tnum text-slate-500">{idx + 1}</td>
                        {/* The Circuit + Products export carries the invoice, recipient, item
                            count and product lines; every cell is shown in full (wrapped), and
                            the pickup store is named on the row as well as in the group heading. */}
                        <EditableCell value={hr["Invoice/PO"] || ""} field="Invoice/PO" readOnly={!canEdit} onSave={(f, o, n) => handleCorrection(hr._idx, f, o, n, hr["Job No"])} className="px-3 py-2.5 font-mono text-xs text-indigo-400 whitespace-nowrap" />
                        <EditableCell value={hr["Customer"] || ""} field="Customer" readOnly={!canEdit} onSave={(f, o, n) => handleCorrection(hr._idx, f, o, n, hr["Job No"])} className="px-3 py-2.5 whitespace-nowrap"
                          render={(v) => <Highlight text={v} query={hnFilter} />} />
                        <EditableCell value={hr["Address Line"]} field="Address Line" readOnly={!canEdit} onSave={(f, o, n) => handleCorrection(hr._idx, f, o, n, hr["Job No"])} className="px-3 py-2.5 font-medium text-white min-w-[180px] max-w-[260px] whitespace-pre-wrap" title={hr["Address Line"]}
                          render={(v) => <Highlight text={v} query={hnFilter} />} />
                        <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                          <span className="font-semibold text-emerald-300">{hr.Store || "—"}</span>
                          {hr["Warehouse Name"] ? <span className="text-slate-500"> · {hr["Warehouse Name"]}</span> : null}
                        </td>
                        <EditableCell value={hr["Recipient Phone Number"]} field="Recipient Phone Number" readOnly={!canEdit} onSave={(f, o, n) => handleCorrection(hr._idx, f, o, n, hr["Job No"])} className="px-3 py-2.5 font-mono text-xs whitespace-nowrap" />
                        <td className="px-3 py-2.5 tnum text-xs text-center">{hr.Items || "—"}</td>
                        <EditableCell value={hr["Products"] || ""} field="Products" readOnly={!canEdit} onSave={(f, o, n) => handleCorrection(hr._idx, f, o, n, hr["Job No"])}
                          className="px-3 py-2.5 text-xs min-w-[220px] max-w-[340px]" title={hr["Products"] || "No product lines in the portal export"}
                          render={(v) => (v ? productLines(v).map((line, i) => <div key={i} className="whitespace-normal"><Highlight text={line} query={hnFilter} /></div>) : <span className="text-slate-600">—</span>)} />
                        <EditableCell value={hr["Instructions"]} field="Instructions" readOnly={!canEdit} onSave={(f, o, n) => handleCorrection(hr._idx, f, o, n, hr["Job No"])} className="px-3 py-2.5 text-slate-400 text-xs min-w-[200px] max-w-[320px] whitespace-pre-wrap" title={hr["Instructions"]} />
                        <td className="px-3 py-2.5 text-[11px] text-slate-500 min-w-[140px] max-w-[240px] whitespace-pre-wrap" title="The portal's own notes on this job (reschedules, ATL)">{hr["Driver Notes"] || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      {activeTab === "manual" && !canEdit && (
        <div className="bg-slate-800/50 rounded-xl p-8 border border-dashed border-slate-700 text-center text-sm text-slate-400">
          Uploading a document creates orders, so it is view only for this account.
          Ask an admin or IT to import a run sheet or email.
        </div>
      )}

      {activeTab === "manual" && canEdit && (
        <div className="space-y-6">
          <div
            className={`bg-slate-800/50 rounded-xl p-8 border border-dashed text-center transition-all ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input 
              type="file" 
              id="file-upload" 
              className="hidden" 
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.eml"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <label htmlFor="file-upload" className={`cursor-pointer inline-flex flex-col items-center justify-center ${uploading ? 'opacity-50 pointer-events-none' : 'hover:opacity-80'}`}>
              <div className="bg-blue-600/20 p-4 rounded-full mb-4">
                {uploading ? <CircleDashed className="animate-spin text-blue-400" size={32} /> : <Upload className="text-blue-400" size={32} />}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                {uploading ? "Extracting Details..." : "Upload Order Document or Email"}
              </h3>
              <p className="text-slate-400 text-sm max-w-sm">
                Drag & drop or click to browse. Supports PDF, EML (Outlook/Gmail), PNG, and JPG. Our AI will automatically extract order details.
              </p>
            </label>
          </div>

          {manualJobs.length > 0 && (
            <div className="bg-slate-800/50 p-4 rounded-xl border border-blue-500/30">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h3 className="text-sm font-bold text-blue-300 uppercase tracking-wider">
                  Imported this session — assign to a run ({manualJobs.length})
                </h3>
                <button onClick={() => setManualJobs([])} className="ml-auto text-xs text-slate-500 hover:text-white">
                  Clear list
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mb-2">
                Saved as orders. Fix any field with Edit, then Assign — the same controls as the unassigned pool.
              </p>
              <div className="space-y-2">
                {manualJobs.map(j => (
                  <PoolRow key={j.id} job={j} runs={runs}
                    onChanged={onManualChanged} onError={reportError} onNotice={notify} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
